import React, { forwardRef, ReactElement, useImperativeHandle, useRef, useState } from 'react';
import { Modal } from 'react-native';
import Overlay from '../../common/Overlay';
import ModalViewport from '../../common/ModalViewport';
import PhonePopup from './profilepopups/PhonePopup';
import ProfileMemberPopup from './profilepopups/ProfileMemberPopup';
import { ProfileMemberItemProps } from './ProfileMemberItem';
import NamePopup from './profilepopups/NamePopup';
import { hasCompleteProfileName } from '../../../models/helpers/profileNameHelper';
import { getMissingRequiredProfileKeys } from '../../../helpers/requiredProfileMembers';

export interface ProfileManagerHandles {
  showNamePopup: () => void;
  showPhoneNumberPopup: () => void;
  showProfileMemberPopup: (profileProps: ProfileMemberItemProps, callback?, options?: { suspendDrawer?: boolean }) => void;
  verifyUserCanAcceptMission: (userPrefs, callback, options?: { suspendDrawer?: boolean }) => boolean;
  verifyUserCanCashOut: (userPrefs, callback) => boolean;
}

const ProfileManager = forwardRef((props: { onVisibilityChange?(visible: boolean): void }, ref) => {
  const [profileModal, setProfileModal] = useState<ReactElement>();
  const [visible, setVisibleState] = useState(false);
  const hideCloseButtonRef = useRef(false);
  const visibilityCallback = useRef(props.onVisibilityChange);
  visibilityCallback.current = props.onVisibilityChange;
  const setVisible = (value: boolean) => {
    setVisibleState(value);
    visibilityCallback.current?.(value);
  };

  const handleOnClose = () => {
    setVisible(false);
    setProfileModal(undefined);
  };

  const handleDismiss = () => {
    if (!hideCloseButtonRef.current) handleOnClose();
  };

  useImperativeHandle(ref, (): ProfileManagerHandles => {
    return {
      showNamePopup() {
        hideCloseButtonRef.current = false;
        setProfileModal(<NamePopup onClose={handleOnClose} />);
        setVisible(true);
      },
      showPhoneNumberPopup() {
        hideCloseButtonRef.current = false;
        setProfileModal(<PhonePopup onClose={handleOnClose} />);
        setVisible(true);
      },
      showProfileMemberPopup(profileProps: ProfileMemberItemProps, callback?, options?: { suspendDrawer?: boolean }) {
        hideCloseButtonRef.current = !!profileProps.hideCloseButton;
        setProfileModal(
          <ProfileMemberPopup
            profileProps={profileProps}
            suspendDrawer={options?.suspendDrawer}
            onClose={handleOnClose}
            onConfirm={() => {
              callback && callback();
            }}
          />,
        );
        setVisible(true);
      },
      verifyUserCanAcceptMission(userPrefs, callback, options?: { suspendDrawer?: boolean }) {
        let requiredProfileMembersMaxPage = getMissingRequiredProfileKeys(userPrefs).length;

        if (!hasCompleteProfileName(userPrefs)) {
          requiredProfileMembersMaxPage++;
        }

        if (userPrefs.phoneNumber === undefined || userPrefs.phoneNumber === '') {
          requiredProfileMembersMaxPage++;
        }

        if (requiredProfileMembersMaxPage == 0) {
          return true;
        }

        fillBasicProfileMembers(userPrefs, 1, requiredProfileMembersMaxPage, callback, options);

        return false;
      },
      verifyUserCanCashOut(userPrefs, callback) {
        hideCloseButtonRef.current = false;
        if (!hasCompleteProfileName(userPrefs)) {
          setProfileModal(
            <NamePopup
              onClose={handleOnClose}
              onConfirm={(userPrefs) => {
                callback && callback(userPrefs);
              }}
            />,
          );
          setVisible(true);

          return false;
        }
        return true;
      },
    };
  }, []);

  const fillBasicProfileMembers = (userPrefs, currentPage, maxPage, callback, options?: { suspendDrawer?: boolean }) => {
    hideCloseButtonRef.current = false;
    if (!hasCompleteProfileName(userPrefs)) {
      setProfileModal(
        <NamePopup
          suspendDrawer={options?.suspendDrawer}
          height={370}
          currentPage={currentPage}
          maxPage={maxPage}
          onClose={handleOnClose}
          onConfirm={(userPrefs) => {
            fillBasicProfileMembers(userPrefs, currentPage + 1, maxPage, callback, options);
          }}
        />,
      );
      setVisible(true);
    } else if (userPrefs.phoneNumber === undefined || userPrefs.phoneNumber === '') {
      setProfileModal(
        <PhonePopup
          suspendDrawer={options?.suspendDrawer}
          height={430}
          currentPage={currentPage}
          maxPage={maxPage}
          onClose={handleOnClose}
          onConfirm={(userPrefs) => {
            fillBasicProfileMembers(userPrefs, currentPage + 1, maxPage, callback, options);
          }}
        />,
      );
      setVisible(true);
    } else {
      const missingKey = getMissingRequiredProfileKeys(userPrefs)[0];
      const profileMember = userPrefs.profileMembers?.find((member) => member.key === missingKey);

      if (profileMember) {
        const remainingPages = getMissingRequiredProfileKeys(userPrefs).length;
        const updatedMaxPage = Math.max(maxPage, currentPage + remainingPages - 1);
        let newProps: ProfileMemberItemProps = {
          profileMember: profileMember,
          value: '',
        };

        setProfileModal(
          <ProfileMemberPopup
            profileProps={newProps}
            suspendDrawer={options?.suspendDrawer}
            height={370}
            currentPage={currentPage}
            maxPage={updatedMaxPage}
            onClose={handleOnClose}
            onConfirm={(userPrefs) => {
              setTimeout(() => {
                fillBasicProfileMembers(userPrefs, currentPage + 1, updatedMaxPage, callback, options);
              }, 300);
            }}
          />,
        );

        setVisible(true);
      } else {
        callback && callback(userPrefs);
      }
    }
  };

  return (
    <Modal animationType="none" visible={visible} transparent={true} onRequestClose={handleDismiss}>
      <ModalViewport>
        <Overlay showOverLay={true} zIndex={1998} onPress={handleDismiss}>
          {profileModal}
        </Overlay>
      </ModalViewport>
    </Modal>
  );
});

export default ProfileManager;
